import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import { usd } from '../lib/format';
import { MARKETING_ORIGIN } from '../lib/site';

const DISMISS_KEY = 'parkingpay.web.promoDismissed';

/** Where "Get the app" sends people. Set once the store listings exist. */
const APP_LINK = `${MARKETING_ORIGIN}/app`;

interface ActivePromotion {
  id: string;
  name: string;
  grantCents: number;
  fundsAvailable: boolean;
}

/**
 * Advertises the app's welcome credit. Purely a poster: it claims nothing,
 * records no device, and the credit only exists once the app is installed and
 * asks for it — so nothing here can be farmed by reloading the page.
 *
 * Hidden the moment the campaign is off or its budget is spent, because an
 * offer that cannot be honoured is worse than no offer.
 */
export function PromoBanner({ lotId }: { lotId?: string }) {
  const { t } = useTranslation();
  const [promotion, setPromotion] = useState<ActivePromotion | null>(null);
  const [hidden, setHidden] = useState(() => localStorage.getItem(DISMISS_KEY) === '1');

  useEffect(() => {
    if (hidden || !lotId) return;
    const controller = new AbortController();
    void api
      .get<{ promotion: ActivePromotion | null }>(
        `/promotions/active?lotId=${encodeURIComponent(lotId)}`,
        controller.signal,
      )
      .then((result) => setPromotion(result.promotion))
      // A promotion is decoration. It must never be why the page looks broken.
      .catch(() => setPromotion(null));
    return () => controller.abort();
  }, [hidden, lotId]);

  if (hidden || !promotion || !promotion.fundsAvailable) return null;

  return (
    <aside className="flex items-center gap-3 rounded-lg border-[1.5px] border-ink bg-ink p-3 text-white">
      <span className="num shrink-0 text-2xl font-bold">{usd(promotion.grantCents)}</span>
      <div className="flex-1">
        <p className="text-sm font-semibold">{t('promo.title', { amount: usd(promotion.grantCents) })}</p>
        <p className="text-xs opacity-75">{t('promo.body')}</p>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        {/* min-h-11 keeps the store link on the 44px touch target (review §5). */}
        <a
          href={APP_LINK}
          className="flex min-h-11 items-center rounded-lg bg-white px-3 text-xs font-semibold text-ink"
        >
          {t('promo.cta')}
        </a>
        <button
          type="button"
          className="text-[11px] opacity-60"
          onClick={() => {
            localStorage.setItem(DISMISS_KEY, '1');
            setHidden(true);
          }}
        >
          {t('promo.dismiss')}
        </button>
      </div>
    </aside>
  );
}
